import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TestimonialsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const translations = {
    en: {
      title: "Success Stories from Industry Leaders",
      subtitle: "See how aluminum manufacturers across India have transformed their operations with our ERP system",
      testimonials: [
        {
          name: "Rajesh Patel",
          position: "Managing Director",
          company: "Gujarat Aluminum Industries",
          location: "Ahmedabad, Gujarat",
          image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
          quote: `Our production efficiency increased by 35% within 6 months of implementing this ERP system. The automated scheduling and real-time monitoring have completely transformed our operations. We've reduced manual errors by 80% and our customer satisfaction scores have never been higher.`,
          metrics: {
            efficiency: "35%",
            errorReduction: "80%",
            costSavings: "₹18L annually"
          },
          videoThumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=225&fit=crop"
        },
        {
          name: "Priya Sharma",
          position: "Operations Head",
          company: "Maharashtra Extrusions Pvt Ltd",
          location: "Pune, Maharashtra",
          image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
          quote: `The quality control module has been a game-changer for us. We've reduced defect rates from 8% to just 1.2%, and our first-pass yield has improved dramatically. The system pays for itself through quality improvements alone.`,
          metrics: {
            defectReduction: "85%",
            qualityScore: "98.5%",
            costSavings: "₹22L annually"
          },
          videoThumbnail: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&h=225&fit=crop"
        },
        {
          name: "Amit Kumar",
          position: "Production Manager",
          company: "South India Aluminum Works",
          location: "Chennai, Tamil Nadu",
          image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
          quote: `Implementation was smooth and the support team was exceptional. Within 3 months, we saw significant improvements in inventory management and production planning. Our material waste has reduced by 40% and delivery times have improved by 50%.`,
          metrics: {
            wasteReduction: "40%",
            deliveryImprovement: "50%",
            costSavings: "₹15L annually"
          },
          videoThumbnail: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&h=225&fit=crop"
        }
      ],
      clientLogos: [
        { name: "Gujarat Aluminum Industries", logo: "https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" },
        { name: "Maharashtra Extrusions", logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" },
        { name: "South India Aluminum", logo: "https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg" },
        { name: "Rajasthan Metal Works", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7c/Siemens-logo.svg" },
        { name: "Karnataka Profiles", logo: "https://upload.wikimedia.org/wikipedia/commons/0/0c/Lenovo_logo_2015.svg" },
        { name: "Delhi Aluminum Co", logo: "https://upload.wikimedia.org/wikipedia/commons/b/bd/Tesla_Motors.svg" }
      ],
      watchVideo: "Watch Video",
      nextTestimonial: "Next",
      prevTestimonial: "Previous"
    },
    hi: {
      title: "उद्योग के नेताओं की सफलता की कहानियां",
      subtitle: "देखें कि भारत भर के एल्यूमीनियम निर्माताओं ने हमारे ERP सिस्टम के साथ अपने संचालन को कैसे बदला है",
      testimonials: [
        {
          name: "राजेश पटेल",
          position: "प्रबंध निदेशक",
          company: "गुजरात एल्यूमीनियम इंडस्ट्रीज",
          location: "अहमदाबाद, गुजरात",
          image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
          quote: `इस ERP सिस्टम को लागू करने के 6 महीने के भीतर हमारी उत्पादन दक्षता 35% बढ़ गई। स्वचालित शेड्यूलिंग और रीयल-टाइम मॉनिटरिंग ने हमारे संचालन को पूरी तरह से बदल दिया है। हमने मैन्युअल त्रुटियों को 80% कम किया है और हमारे ग्राहक संतुष्टि स्कोर कभी इतने ऊंचे नहीं रहे।`,
          metrics: {
            efficiency: "35%",
            errorReduction: "80%",
            costSavings: "₹18L वार्षिक"
          },
          videoThumbnail: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=225&fit=crop"
        },
        {
          name: "प्रिया शर्मा",
          position: "संचालन प्रमुख",
          company: "महाराष्ट्र एक्सट्रूज़न प्राइवेट लिमिटेड",
          location: "पुणे, महाराष्ट्र",
          image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
          quote: `गुणवत्ता नियंत्रण मॉड्यूल हमारे लिए एक गेम-चेंजर रहा है। हमने दोष दर को 8% से घटाकर केवल 1.2% कर दिया है, और हमारी फर्स्ट-पास यील्ड में नाटकीय सुधार हुआ है। यह सिस्टम केवल गुणवत्ता सुधार के माध्यम से अपनी लागत वसूल कर लेता है।`,
          metrics: {
            defectReduction: "85%",
            qualityScore: "98.5%",
            costSavings: "₹22L वार्षिक"
          },
          videoThumbnail: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&h=225&fit=crop"
        },
        {
          name: "अमित कुमार",
          position: "उत्पादन प्रबंधक",
          company: "साउथ इंडिया एल्यूमीनियम वर्क्स",
          location: "चेन्नई, तमिलनाडु",
          image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
          quote: `कार्यान्वयन सुचारू था और सहायता टीम असाधारण थी। 3 महीने के भीतर, हमने इन्वेंटरी प्रबंधन और उत्पादन योजना में महत्वपूर्ण सुधार देखे। हमारी सामग्री बर्बादी 40% कम हो गई है और डिलीवरी समय में 50% सुधार हुआ है।`,
          metrics: {
            wasteReduction: "40%",
            deliveryImprovement: "50%",
            costSavings: "₹15L वार्षिक"
          },
          videoThumbnail: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=400&h=225&fit=crop"
        }
      ],
      clientLogos: [
        { name: "गुजरात एल्यूमीनियम इंडस्ट्रीज", logo: "https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" },
        { name: "महाराष्ट्र एक्सट्रूज़न", logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" },
        { name: "साउथ इंडिया एल्यूमीनियम", logo: "https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg" },
        { name: "राजस्थान मेटल वर्क्स", logo: "https://upload.wikimedia.org/wikipedia/commons/7/7c/Siemens-logo.svg" },
        { name: "कर्नाटक प्रोफाइल्स", logo: "https://upload.wikimedia.org/wikipedia/commons/0/0c/Lenovo_logo_2015.svg" },
        { name: "दिल्ली एल्यूमीनियम कंपनी", logo: "https://upload.wikimedia.org/wikipedia/commons/b/bd/Tesla_Motors.svg" }
      ],
      watchVideo: "वीडियो देखें",
      nextTestimonial: "अगला",
      prevTestimonial: "पिछला"
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setActiveTestimonial(prev => (prev + 1) % t.testimonials.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, t.testimonials.length]);

  const nextTestimonial = () => {
    setActiveTestimonial(prev => (prev + 1) % t.testimonials.length);
    setIsAutoPlaying(false);
  };

  const prevTestimonial = () => {
    setActiveTestimonial(prev => (prev - 1 + t.testimonials.length) % t.testimonials.length);
    setIsAutoPlaying(false);
  };

  const currentTestimonial = t.testimonials[activeTestimonial];

  return (
    <section id="success-stories" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden mb-16">
          <div className="grid lg:grid-cols-2">
            {/* Content Side */}
            <div className="p-8 lg:p-12">
              <div className="space-y-6">
                {/* Quote */}
                <div className="relative">
                  <Icon 
                    name="Quote" 
                    size={40} 
                    color="var(--color-primary-200)" 
                    className="absolute -top-2 -left-2"
                  />
                  <blockquote className="text-lg text-text-secondary leading-relaxed pl-8">
                    {currentTestimonial.quote}
                  </blockquote>
                </div>

                {/* Author Info */}
                <div className="flex items-center space-x-4">
                  <Image
                    src={currentTestimonial.image}
                    alt={currentTestimonial.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="text-lg font-semibold text-primary">
                      {currentTestimonial.name}
                    </h4>
                    <p className="text-text-secondary">
                      {currentTestimonial.position}
                    </p>
                    <p className="text-sm text-text-muted">
                      {currentTestimonial.company}, {currentTestimonial.location}
                    </p>
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                  {Object.entries(currentTestimonial.metrics).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-2xl font-bold text-success mb-1">
                        {value}
                      </div>
                      <div className="text-xs text-text-secondary capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Video Side */}
            <div className="relative bg-surface-100 lg:bg-transparent">
              <div className="aspect-video lg:aspect-auto lg:h-full relative overflow-hidden">
                <Image
                  src={currentTestimonial.videoThumbnail}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <Button
                    variant="primary"
                    size="lg"
                    className="bg-white text-primary hover:bg-surface-100 rounded-full w-16 h-16 p-0"
                    iconName="Play"
                    iconSize={24}
                  />
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <Button
                    variant="ghost"
                    className="text-white hover:bg-white hover:bg-opacity-20"
                    iconName="Play"
                    iconPosition="left"
                    iconSize={16}
                  >
                    {t.watchVideo}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center space-x-4 mb-16">
          <Button
            variant="outline"
            onClick={prevTestimonial}
            className="rounded-full w-12 h-12 p-0"
            iconName="ChevronLeft"
            iconSize={20}
          />
          
          <div className="flex space-x-2">
            {t.testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setActiveTestimonial(index);
                  setIsAutoPlaying(false);
                }}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeTestimonial
                    ? 'bg-primary w-8' :'bg-surface-200 hover:bg-surface-300'
                }`}
              />
            ))}
          </div>
          
          <Button
            variant="outline"
            onClick={nextTestimonial}
            className="rounded-full w-12 h-12 p-0"
            iconName="ChevronRight"
            iconSize={20}
          />
        </div>

        {/* Trusted by Industry Leaders - Ultra Modern UI */}
        <div className="relative bg-white/60 backdrop-blur-xl rounded-2xl p-10 mt-20 shadow-cta border border-border overflow-hidden">
          {/* Gradient Accent Line */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent-400 via-primary-400 to-accent-600 opacity-80 animate-pulse" />
          <div className="text-center mb-8 relative z-10">
            <h3 className="text-2xl md:text-3xl font-extrabold text-primary mb-2 tracking-tight drop-shadow-sm">
              {currentLanguage === 'en' ? 'Trusted by Industry Leaders' : 'उद्योग के नेताओं द्वारा भरोसा'}
            </h3>
            <p className="text-text-secondary text-base md:text-lg font-medium">
              {currentLanguage === 'en' ?'Join 500+ aluminum manufacturers who trust our ERP system' :'500+ एल्यूमीनियम निर्माताओं में शामिल हों जो हमारे ERP सिस्टम पर भरोसा करते हैं'}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 items-center relative z-10">
            {t.clientLogos.map((client, index) => (
              <div
                key={index}
                className="flex items-center justify-center p-4 bg-white rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer hover:scale-105 border border-border"
                style={{ minHeight: '90px', minWidth: '90px' }}
              >
                <Image
                  src={client.logo}
                  alt={client.name}
                  className="max-w-[60px] max-h-[60px] object-contain drop-shadow-md"
                />
              </div>
            ))}
          </div>
          {/* Decorative Gradient Blob */}
          <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-gradient-to-tr from-accent-200 via-accent-400 to-accent-600 rounded-full opacity-20 blur-3xl -z-10 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;